/**
 * Standardized locale data for the application
 * This is the centralized source for all language and country data
 */

import countries from "i18n-iso-countries";
import ISO6391 from "iso-639-1";

// Register English locale for country names
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

export type LocaleOption = {
  label: string;
  value: string;
};

// Dynamically generate full ISO 3166-1 country list, sorted by label
export const COUNTRY_OPTIONS: LocaleOption[] = Object.entries(
  countries.getNames("en")
)
  .map(([value, label]: [string, unknown]) => ({ label: String(label), value })) // label is unknown, cast to string
  .sort((a: LocaleOption, b: LocaleOption) => a.label.localeCompare(b.label));

// Dynamically generate full ISO 639-1 language list, sorted by label
export const LANGUAGE_OPTIONS: LocaleOption[] = ISO6391.getAllCodes()
  .map((code: string) => ({
    label: ISO6391.getName(code) as string,
    value: code,
  }))
  .filter((opt: LocaleOption) => !!opt.label)
  .sort((a: LocaleOption, b: LocaleOption) => a.label.localeCompare(b.label));

// Helper functions
export function getLabelFromValue(
  options: LocaleOption[],
  value: string
): string {
  const option = options.find((opt) => opt.value === value);
  return option ? option.label : value;
}

export function getValueFromLabel(
  options: LocaleOption[],
  label: string
): string {
  const option = options.find((opt) => opt.label === label);
  return option ? option.value : "";
}

// Convert simple strings to locale options
export function stringToLocaleOption(str: string): LocaleOption {
  return { label: str, value: str.toLowerCase() };
}

// Convert simple string array to locale options
export function stringsToLocaleOptions(strings: string[]): LocaleOption[] {
  return strings.map((str) => stringToLocaleOption(str));
}

// Stripe-supported countries for Express accounts
// Source: https://stripe.com/docs/connect/account-types#supported-countries
export const stripeSupportedCountries = new Set([
  "US",
  "CA",
  "GB",
  "AU",
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "CH",
  "NO",
  "BR",
  "MX",
  "AR",
  "CL",
  "CO",
  "PE",
  "UY",
  "JP",
  "SG",
  "HK",
  "MY",
  "PH",
  "TH",
  "VN",
  "ID",
  "IN",
  "NZ",
  "AE",
  "SA",
  "IL",
  "TR",
  "ZA",
  "NG",
  "KE",
  "GH",
  "UG",
  "TZ",
  "RW",
  "ZM",
  "MW",
  "BW",
  "NA",
  "LS",
  "SZ",
  "MZ",
  "AO",
  "ZW",
  "MG",
  "MU",
  "SC",
  "KM",
  "DJ",
  "ET",
  "SO",
  "SD",
  "SS",
  "CF",
  "TD",
  "CM",
  "GQ",
  "GA",
  "CG",
  "CD",
  "BI",
  "RW",
  "TZ",
  "KE",
  "UG",
  "ET",
  "SO",
  "DJ",
  "ER",
  "YE",
  "OM",
  "AE",
  "QA",
  "BH",
  "KW",
  "SA",
  "JO",
  "LB",
  "SY",
  "IQ",
  "IR",
  "AF",
  "PK",
  "IN",
  "BD",
  "LK",
  "MV",
  "NP",
  "BT",
  "MM",
  "TH",
  "LA",
  "VN",
  "KH",
  "MY",
  "SG",
  "BN",
  "ID",
  "PH",
  "TL",
  "PG",
  "FJ",
  "NC",
  "VU",
  "SB",
  "TO",
  "WS",
  "AS",
  "GU",
  "MP",
  "FM",
  "MH",
  "PW",
  "CK",
  "NU",
  "TK",
  "TV",
  "NR",
  "KI",
  "WS",
  "TO",
  "FJ",
  "VU",
  "NC",
  "PG",
  "TL",
  "ID",
  "PH",
  "BN",
  "SG",
  "MY",
  "KH",
  "VN",
  "LA",
  "TH",
  "MM",
  "BT",
  "NP",
  "MV",
  "LK",
  "BD",
  "IN",
  "PK",
  "AF",
  "IR",
  "IQ",
  "SY",
  "LB",
  "JO",
  "SA",
  "KW",
  "BH",
  "QA",
  "AE",
  "OM",
  "YE",
  "ER",
  "DJ",
  "SO",
  "ET",
  "UG",
  "KE",
  "TZ",
  "RW",
  "BI",
  "CD",
  "CG",
  "GA",
  "GQ",
  "CM",
  "TD",
  "CF",
  "SS",
  "SD",
  "SO",
  "ET",
  "DJ",
  "KM",
  "SC",
  "MU",
  "MG",
  "ZW",
  "AO",
  "MZ",
  "SZ",
  "LS",
  "NA",
  "BW",
  "MW",
  "ZM",
  "RW",
  "TZ",
  "KE",
  "UG",
  "GH",
]);

// Unified country utilities
export function getCountryCode(countryName: string): string {
  if (!countryName) {
    throw new Error("Country name is required");
  }

  // If the input is already a 2-letter code, validate and return it
  if (
    countryName.length === 2 &&
    /^[A-Z]{2}$/.test(countryName.toUpperCase())
  ) {
    return countryName.toUpperCase();
  }

  // Try to find by label (country name)
  const country = COUNTRY_OPTIONS.find(
    (option) => option.label.toLowerCase() === countryName.toLowerCase()
  );
  if (country) return country.value;

  // Try to find by value (country code)
  const countryByCode = COUNTRY_OPTIONS.find(
    (option) => option.value.toLowerCase() === countryName.toLowerCase()
  );
  if (countryByCode) return countryByCode.value;

  // If no match is found, throw an error
  throw new Error(
    `Could not find country code for "${countryName}". Please use a valid country name or 2-letter ISO country code.`
  );
}

export function getCountryName(countryCode: string): string {
  if (!countryCode) {
    throw new Error("Country code is required");
  }

  // Try to find by value (country code)
  const country = COUNTRY_OPTIONS.find(
    (option) => option.value.toLowerCase() === countryCode.toLowerCase()
  );
  if (country) return country.label;

  // If no match is found, return the original code
  return countryCode;
}

export function validateStripeCountry(countryCode: string): boolean {
  return stripeSupportedCountries.has(countryCode.toUpperCase());
}

export function getStripeSupportedCountryCode(countryName: string): string {
  const countryCode = getCountryCode(countryName);

  if (!validateStripeCountry(countryCode)) {
    throw new Error(
      `Country "${countryName}" (${countryCode}) is not currently supported by Stripe for Express accounts. Please choose a supported country like United States, United Kingdom, Canada, Australia, Germany, France, or other major countries.`
    );
  }

  return countryCode;
}

export function getStripeSupportedCountriesList(): string[] {
  return Array.from(stripeSupportedCountries).sort();
}

export function getStripeSupportedCountryNames(): string[] {
  return getStripeSupportedCountriesList()
    .map((code) => getCountryName(code))
    .sort();
}

export function getStripeSupportedCountryOptions(): LocaleOption[] {
  return COUNTRY_OPTIONS.filter((option) =>
    stripeSupportedCountries.has(option.value.toUpperCase())
  );
}
